import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// 格式化答案：识别编号、加粗、分段
function formatAnswer(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let key = 0;
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      elements.push(<div key={key++} style={{ height: 8 }} />);
      continue;
    }
    
    const numMatch = trimmed.match(/^(\d+)[.、）)\]]\s*/);
    
    if (numMatch && !trimmed.startsWith('根据')) {
      const rest = trimmed.replace(/^(\d+)[.、）)\]]\s*/, '');
      elements.push(
        <div key={key++} className="qa-answer-step">
          <span className="qa-step-num">{numMatch[1]}</span>
          <span className="qa-step-text">{formatInline(rest)}</span>
        </div>
      );
    } else if (trimmed.startsWith('注意') || trimmed.startsWith('提示')) {
      elements.push(
        <div key={key++} className="qa-answer-note">
          ⚠️ {formatInline(trimmed.replace(/^(注意[：:]?|提示[：:]?)\s*/, ''))}
        </div>
      );
    } else if (trimmed.startsWith('根据')) {
      elements.push(
        <div key={key++} className="qa-answer-reference">
          📋 {formatInline(trimmed)}
        </div>
      );
    } else {
      elements.push(
        <div key={key++} className="qa-answer-line">{formatInline(trimmed)}</div>
      );
    }
  }
  
  return elements;
}

function formatInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export default function KnowledgeQA() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState([]);
  const [sourceType, setSourceType] = useState('local');
  const [sourceLabel, setSourceLabel] = useState('');
  const [disclaimer, setDisclaimer] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('qa_history');
    if (saved) {
      try { setHistory(JSON.parse(saved)); } catch {}
    }
  }, []);

  const handleAsk = async () => {
    if (!question.trim()) return;
    
    setLoading(true);
    setAnswer('');
    setSources([]);
    setSourceType('local');
    setSourceLabel('');
    setDisclaimer('');
    
    try {
      const resp = await fetch(`${API_URL}/api/knowledge/qa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: question.trim() })
      });
      const data = await resp.json();
      
      if (data.success) {
        setAnswer(data.answer);
        setSources(data.sources || []);
        setSourceType(data.sourceType || 'local');
        setSourceLabel(data.sourceLabel || '');
        setDisclaimer(data.disclaimer || '');
        
        const newHistory = [{ q: question.trim(), a: data.answer, time: new Date().toISOString() }, ...history.slice(0, 19)];
        setHistory(newHistory);
        localStorage.setItem('qa_history', JSON.stringify(newHistory));

        // 异步记录统计（不阻塞UI）
        const currentUser = localStorage.getItem('notes_user') || '';
        fetch(`${API_URL}/api/knowledge/qa/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question: question.trim(), user: currentUser })
        }).catch(() => {});
      } else {
        setAnswer(data.error || '抱歉，无法回答这个问题。请换个方式提问。');
      }
    } catch (e) {
      setAnswer('网络错误，请稍后重试。');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('qa_history');
  };

  return (
    <div className="knowledge-container">
      <div className="kb-browse-header">
        <button className="kb-back-btn" onClick={() => navigate('/knowledge')}>← 返回</button>
        <h2>🤖 智能问答</h2>
      </div>

      <div className="qa-intro">
        <p>基于云端知识库智能回答用友产品问题，知识库未覆盖时自动使用AI知识</p>
        <div className="qa-sources-list">
          <span className="qa-source-tag">📚 云端知识库</span>
          <span className="qa-source-tag">🤖 AI知识兜底</span>
        </div>
      </div>

      <div className="qa-input-box">
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="输入用友产品相关问题，如：采购订单的打印次数字段在哪张表里？"
          className="qa-input"
          rows={3}
          autoFocus
        />
        <button 
          className="qa-submit-btn" 
          onClick={handleAsk}
          disabled={loading || !question.trim()}
        >
          {loading ? '思考中...' : '提问'}
        </button>
      </div>

      {loading && (
        <div className="qa-loading">
          <div className="knowledge-spinner" />
          <p>正在搜索知识库并分析...</p>
        </div>
      )}

      {answer && !loading && (
        <div className="qa-answer-box">
          <div className="qa-answer-header">
            <span className="qa-answer-icon">💡</span>
            <span>AI回答</span>
            <span className={`qa-answer-badge ${sourceType === 'ai' ? 'qa-badge-ai' : ''}`}>
              {sourceLabel || (sourceType === 'ai' ? '🤖 AI知识' : '📚 云端知识库')}
            </span>
          </div>
          
          <div className="qa-answer-content">
            {formatAnswer(answer)}
          </div>
          
          {disclaimer && (
            <div className="qa-disclaimer">
              ⚠️ {disclaimer}
            </div>
          )}
          
          {sources.length > 0 && (
            <div className="qa-sources">
              <div className="qa-sources-header">
                <span>📚 知识来源</span>
                <span className="qa-sources-count">{Math.min(sources.length, 5)}条相关</span>
              </div>
              <div className="qa-sources-list-detail">
                {sources.slice(0, 5).map((s, idx) => (
                  <div key={idx} className="qa-source-item">
                    <div className="qa-source-num">#{idx + 1}</div>
                    <div className="qa-source-content">
                      <div className="qa-source-q">{s.q}</div>
                      <div className="qa-source-a">
                        {s.a ? (s.a.length > 200 ? s.a.substring(0, 200) + '...' : s.a) : '无答案'}
                      </div>
                      <div className="qa-source-meta">
                        <span className="qa-source-from">📂 {s.source}</span>
                        {s.product && <span className="qa-tag">{s.product}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && !loading && (
        <div className="qa-history">
          <div className="qa-history-header">
            <span>📝 最近提问</span>
            <button className="qa-clear-btn" onClick={clearHistory}>清空</button>
          </div>
          <div className="qa-history-list">
            {history.slice(0, 5).map((h, idx) => (
              <div key={idx} className="qa-history-item" onClick={() => {
                setQuestion(h.q);
                setAnswer('');
                setSources([]);
              }}>
                <span className="qa-history-q">{h.q}</span>
                <span className="qa-history-time">{new Date(h.time).toLocaleDateString('zh-CN')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="qa-suggestions">
        <h4>试试这些问题：</h4>
        <div className="qa-suggestion-list">
          <span className="qa-hint" onClick={() => { setQuestion('采购订单的打印次数字段在哪张表里？'); handleAsk(); }}>采购订单打印次数字段在哪张表？</span>
          <span className="qa-hint" onClick={() => { setQuestion('如何处理销售出库数量与发票数量不一致的情况？'); handleAsk(); }}>销售出库与发票数量不一致</span>
          <span className="qa-hint" onClick={() => { setQuestion('NC Cloud预算执行规则如何设置？'); handleAsk(); }}>预算执行规则设置</span>
          <span className="qa-hint" onClick={() => { setQuestion('合并报表抵销模板金额规则怎么取值？'); handleAsk(); }}>合并报表抵销模板取值</span>
        </div>
      </div>
    </div>
  );
}